import React, { useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import MoveDownIcon from '@mui/icons-material/MoveDown';
import { useUser } from '../../contexts/UserContext';
import { isPwa } from '../../utils/isPwa';
import BaseDialog from '../dialogs/BaseDialog';

/**
 * ゲストモード用のグローバルUI。
 * - ゲスト利用中バナー（画面下部固定）: 登録でデータが保存されることを常時案内
 * - 登録促しダイアログ: ゲスト不可の機能に触れた際（APIが guest_forbidden の403を
 *   返し、api.ts が 'guestUpgradeRequired' イベントを発火）に表示
 */
const GuestModeUI: React.FC = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [existingAccountDialogOpen, setExistingAccountDialogOpen] = useState(false);

  useEffect(() => {
    const handleUpgradeRequired = () => setUpgradeDialogOpen(true);
    const handleExistingAccount = () => setExistingAccountDialogOpen(true);
    window.addEventListener('guestUpgradeRequired', handleUpgradeRequired);
    window.addEventListener('guestExistingAccount', handleExistingAccount);
    return () => {
      window.removeEventListener('guestUpgradeRequired', handleUpgradeRequired);
      window.removeEventListener('guestExistingAccount', handleExistingAccount);
    };
  }, []);

  const isGuest = user?.is_guest === true;

  const goToLogin = () => {
    setUpgradeDialogOpen(false);
    navigate('/login');
  };

  return (
    <>
      {isGuest && (
        <Box
          sx={{
            position: 'fixed',
            left: 0,
            right: 0,
            // PWAではボトムナビ（高さ56px＋セーフエリア）の直上に隙間なく置く
            bottom: isPwa() ? 'calc(56px + env(safe-area-inset-bottom))' : 0,
            // ダイアログ(modal=1300)より必ず下にする。上に置くとフルスクリーン表示の
            // レビューダイアログ等で下部の操作ボタンが押せなくなる
            zIndex: (theme) => theme.zIndex.appBar,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            px: 2,
            py: 1,
            backgroundColor: 'rgba(142, 163, 38, 0.95)',
            color: '#fff',
          }}
        >
          {/* 幅が足りないときはテキスト側を縮める（minWidth:0 がないとflexは縮まない） */}
          <Typography
            variant="body2"
            sx={{ fontWeight: 500, minWidth: 0, lineHeight: 1.3, fontSize: { xs: '0.7rem', sm: '0.875rem' } }}
          >
            ゲスト利用中 — 登録してライブラリを引き継げます
          </Typography>
          <Button
            size="small"
            variant="contained"
            disableElevation
            startIcon={<PersonAddAltIcon />}
            onClick={goToLogin}
            sx={{
              backgroundColor: '#fff',
              color: 'rgba(0,0,0,0.85)',
              whiteSpace: 'nowrap',
              // 縮ませない。既定の flex-shrink:1 のままだと狭い画面でボタンが潰れ、
              // nowrap の文字がはみ出して左右のパディングが無くなったように見える
              flexShrink: 0,
              px: 1.5,
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.85)' },
            }}
          >
            登録する
          </Button>
        </Box>
      )}

      <BaseDialog
        open={upgradeDialogOpen}
        onClose={() => setUpgradeDialogOpen(false)}
        title="アカウント登録が必要です"
        maxWidth="sm"
        actions={
          <>
            <Button onClick={() => setUpgradeDialogOpen(false)} color="inherit">
              閉じる
            </Button>
            <Button onClick={goToLogin} variant="contained">
              登録する
            </Button>
          </>
        }
      >
        <Typography variant="body2" sx={{ mb: 2 }}>
          この機能は<strong>ゲストではご利用いただけません</strong>。
        </Typography>

        {/* 「今できること」と「登録で解放されること」を対比で見せる（平文だと読み飛ばされるため） */}
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="caption"
            sx={{ display: 'block', mb: 0.5, color: 'text.secondary', fontWeight: 600 }}
          >
            ゲストで利用できるのは…
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 18, color: 'success.main', mt: '2px', flexShrink: 0 }} />
            <Typography variant="body2">
              ゲームライブラリの基本機能（ゲームの登録・スコアやプレイ日の記録）
            </Typography>
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography
            variant="caption"
            sx={{ display: 'block', mb: 0.5, color: 'text.secondary', fontWeight: 600 }}
          >
            アカウント登録（無料）で使えるようになる機能の一部
          </Typography>
          {[
            'ライブラリの公開・共有',
            '文章レビュー・非公開メモなどの詳細な記録',
            'フォローやタイムラインなどのコミュニティ機能',
            'アニメや映画も記録できる拡張ライブラリ',
            'ランキングやTierリスト・GOTYの作成',
            'プレイログで毎日のプレイ時間を計測',
            'Steamライブラリの自動同期 …など',
          ].map((item) => (
            <Box key={item} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
              <LockOutlinedIcon sx={{ fontSize: 18, color: 'text.disabled', mt: '2px', flexShrink: 0 }} />
              <Typography variant="body2" color="text.secondary">
                {item}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* 一番伝えたい一点（データが消えない）は枠で隔離して視線を止める */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 1.25,
            borderRadius: 1,
            borderLeft: '3px solid',
            borderColor: 'primary.main',
            backgroundColor: 'action.hover',
          }}
        >
          <MoveDownIcon sx={{ fontSize: 20, color: 'primary.main', flexShrink: 0 }} />
          <Typography variant="body2">
            ゲストで記録したライブラリは<strong>そのまま引き継ぐことができます</strong>
          </Typography>
        </Box>
      </BaseDialog>

      {/* ゲストが既存アカウントでログインしようとしたとき（サーバーが409で停止）。
          ここでは破棄を実行させない（ワンタップで消せるボタンは置かない）。
          破棄したい場合はメニューのログアウト（専用の確認あり）を通す */}
      <BaseDialog
        open={existingAccountDialogOpen}
        onClose={() => setExistingAccountDialogOpen(false)}
        title="このアカウントは既に登録されています"
        maxWidth="sm"
        actions={
          <Button onClick={() => setExistingAccountDialogOpen(false)} variant="contained">
            閉じる
          </Button>
        }
      >
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          このアカウントには既にPULSEアカウントがあるため、
          <strong>ゲストのライブラリを引き継ぐことはできません</strong>。
          ゲストのデータを引き継げるのは、新しいアカウントを作成する場合のみです。
        </Typography>
        <Typography variant="body2">
          この既存アカウントでログインするには、
          <strong>ゲストデータを破棄する</strong>必要があります。破棄する場合は、
          メニューの「ログアウト」から行ってください。破棄したゲストデータは元に戻せません。
        </Typography>
      </BaseDialog>
    </>
  );
};

export default GuestModeUI;
